import type { Uporabnik } from '@koda/shared';
import { useCallback, useEffect, useState, type JSX } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { api, nastaviObIzgubiSeje } from './lib/api';
import { JezikContext, jezikBrskalnika, useBesedila } from './lib/jezik';
import { UporabnikContext, useUporabnik } from './lib/uporabnik';
import { Glava } from './sestavni/Glava';
import { ArhivStran } from './strani/ArhivStran';
import { StanjeBaze } from './strani/StanjeBaze';
import { DnevnikPoste } from './strani/DnevnikPoste';
import { GraditeljKazalnikov } from './strani/GraditeljKazalnikov';
import { Podjetja } from './strani/Podjetja';
import { Podjetje } from './strani/Podjetje';
import { Porocilo } from './strani/Porocilo';
import { Prijava } from './strani/Prijava';
import { Uporabniki } from './strani/Uporabniki';
import { Nastavitve } from './strani/Nastavitve';
import { NastavitveProjektov } from './strani/NastavitveProjektov';
import { NastavitveFcVc } from './strani/NastavitveFcVc';
import { NastavitvePartnerjev } from './strani/NastavitvePartnerjev';
import { ProjektiStran } from './strani/ProjektiStran';
import { NastavitveKazalnikov } from './strani/NastavitveKazalnikov';
import { SplosneNastavitveKazalnikov } from './strani/SplosneNastavitveKazalnikov';
import { Uvoz } from './strani/Uvoz';
import { Vabilo } from './strani/Vabilo';

/**
 * Stran, ki je samo za skrbnika.
 *
 * To je vljudnost vmesnika, ne varovalo: strežnik iste poti zavrne tudi, če
 * bi stranka naslov vtipkala na roko.
 */
function SamoSkrbnik({ stran }: { stran: JSX.Element }): JSX.Element {
  const uporabnik = useUporabnik();
  const b = useBesedila();
  if (uporabnik.jeAdmin) return stran;
  return (
    <main>
      <h1>{b.niDostopa}</h1>
      <p>{b.straneUrejaServis}</p>
    </main>
  );
}

/** Prijavljeni del aplikacije. */
function Aplikacija({
  uporabnik,
  naOdjavo,
  naJezik,
}: {
  uporabnik: Uporabnik;
  naOdjavo: () => void;
  naJezik: () => void;
}): JSX.Element {
  return (
    <UporabnikContext.Provider value={uporabnik}>
      <JezikContext.Provider value={uporabnik.jezik}>
      <Glava uporabnik={uporabnik} naOdjavo={naOdjavo} naJezik={naJezik} />
      <Routes>
        <Route path="/" element={<Podjetja />} />
        <Route path="/uporabniki" element={<SamoSkrbnik stran={<Uporabniki />} />} />
        <Route path="/posta" element={<SamoSkrbnik stran={<DnevnikPoste />} />} />
        <Route path="/arhiv" element={<SamoSkrbnik stran={<ArhivStran />} />} />
        <Route path="/stanje-baze" element={<SamoSkrbnik stran={<StanjeBaze />} />} />
        <Route
          path="/nastavitve/kazalniki"
          element={<SamoSkrbnik stran={<SplosneNastavitveKazalnikov />} />}
        />
        <Route
          path="/nastavitve/kazalniki/graditelj"
          element={<SamoSkrbnik stran={<GraditeljKazalnikov />} />}
        />
        <Route path="/podjetje/:id" element={<Podjetje />} />
        <Route path="/podjetje/:id/porocilo/:vrsta" element={<Porocilo />} />
        <Route path="/podjetje/:id/uvoz" element={<SamoSkrbnik stran={<Uvoz />} />} />
        <Route
          path="/podjetje/:id/nastavitve"
          element={<SamoSkrbnik stran={<Nastavitve />} />}
        />
        <Route
          path="/podjetje/:id/nastavitve/kazalniki"
          element={<SamoSkrbnik stran={<NastavitveKazalnikov />} />}
        />
        <Route
          path="/podjetje/:id/nastavitve/fcvc"
          element={<SamoSkrbnik stran={<NastavitveFcVc />} />}
        />
        {/* Partnerje ureja tudi skrbnik stranke; kdo sme, pove strežnik, stran pa vpogled vedno pokaže. */}
        <Route path="/podjetje/:id/nastavitve/partnerji" element={<NastavitvePartnerjev />} />
        <Route
          path="/podjetje/:id/projekti/nastavitve"
          element={<SamoSkrbnik stran={<NastavitveProjektov />} />}
        />
        <Route path="/podjetje/:id/projekti/:pogled" element={<ProjektiStran />} />
        <Route path="*" element={<Podjetja />} />
      </Routes>
      </JezikContext.Provider>
    </UporabnikContext.Provider>
  );
}

/**
 * Aplikacija v brskalniku.
 *
 * Brez prijave ni nobene strani s podatki: dokler strežnik ne pove, kdo je
 * prijavljen, se pokaže prijava. Edina izjema je sprejem vabila — povabljeni
 * gesla še nima, zato se do te strani pride brez prijave.
 */
export function App(): JSX.Element {
  const [uporabnik, setUporabnik] = useState<Uporabnik | null>(null);
  const [berem, setBerem] = useState(true);

  const osvezi = useCallback((): void => {
    setBerem(true);
    api
      .jaz()
      .then(setUporabnik)
      .catch(() => {
        setUporabnik(null);
      })
      .finally(() => {
        setBerem(false);
      });
  }, []);

  useEffect(osvezi, [osvezi]);

  // Seja lahko poteče ali jo skrbnik prekliče sredi dela; takrat se vmesnik
  // vrne na prijavo, namesto da bi stran ostala z napako.
  useEffect(() => {
    nastaviObIzgubiSeje(() => {
      setUporabnik(null);
    });
  }, []);

  const odjavi = (): void => {
    api.odjava().finally(() => {
      setUporabnik(null);
    });
  };

  // Pred prijavo nastavitve uporabnika ni, zato jezik pove brskalnik.
  const jezik = uporabnik?.jezik ?? jezikBrskalnika();

  const notranjost = berem ? (
    <main>
      <p>…</p>
    </main>
  ) : uporabnik ? (
    <Aplikacija uporabnik={uporabnik} naOdjavo={odjavi} naJezik={osvezi} />
  ) : (
    <Prijava naPrijavo={osvezi} />
  );

  return (
    <JezikContext.Provider value={jezik}>
      <BrowserRouter>
        <Routes>
          <Route path="/vabilo/:zeton" element={<Vabilo naSprejem={osvezi} />} />
          <Route path="/ponastavitev/:zeton" element={<Vabilo key="ponastavitev" vrsta="ponastavitev" naSprejem={osvezi} />} />
          <Route path="*" element={notranjost} />
        </Routes>
      </BrowserRouter>
    </JezikContext.Provider>
  );
}
